import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    VisaBrandIcon,
    ToggleGroup,
    Breadcrumb,
    Separator,
    Progress,
    Sidebar,
    Avatar,
    Button,
    Alert,
    Badge,
    Table,
    Item,
    Card,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    LayoutDashboard,
    TriangleAlert,
    ShieldCheck,
    CreditCard,
    Download,
    Settings,
    Folder,
    Users,
    Check,
    Plug,
    Bell
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type Period = "monthly" | "yearly";

type InvoiceStatus = "paid" | "pending" | "failed";

type Invoice = {
    status: InvoiceStatus;
    amount: number;
    date: Date;
    no: string;
};

type UsageRow = {
    unit?: string;
    limit: number;
    used: number;
    id: string;
};

type Plan = {
    features: Array<string>;
    isCurrent: boolean;
    id: string;
};

const LayoutDashboardIcon = createKitIcon(LayoutDashboard);

const TriangleAlertIcon = createKitIcon(TriangleAlert);

const ShieldCheckIcon = createKitIcon(ShieldCheck);

const CreditCardIcon = createKitIcon(CreditCard);

const DownloadIcon = createKitIcon(Download);

const SettingsIcon = createKitIcon(Settings);

const FolderIcon = createKitIcon(Folder);

const UsersIcon = createKitIcon(Users);

const PlugIcon = createKitIcon(Plug);

const BellIcon = createKitIcon(Bell);

const PERIODS: Array<Period> = [
    "monthly",
    "yearly"
];

const USAGE: Array<UsageRow> = [
    {
        id: "members",
        limit: 25,
        used: 18
    },
    {
        id: "storage",
        limit: 100,
        used: 64.2,
        unit: "GB"
    },
    {
        id: "automations",
        limit: 10000,
        used: 8420
    }
];

const WARNING_PERCENT = 80;

const PLANS: Array<Plan> = [
    {
        features: [
            "feature_starter1",
            "feature_starter2",
            "feature_starter3",
            "feature_starter4"
        ],
        isCurrent: false,
        id: "starter"
    },
    {
        features: [
            "feature_team1",
            "feature_team2",
            "feature_team3",
            "feature_team4",
            "feature_team5"
        ],
        isCurrent: true,
        id: "team"
    },
    {
        features: [
            "feature_business1",
            "feature_business2",
            "feature_business3",
            "feature_business4",
            "feature_business5"
        ],
        isCurrent: false,
        id: "business"
    }
];

const INVOICES: Array<Invoice> = [
    {
        date: new Date(2026, 10, 1),
        no: "FTR-2026-0011",
        status: "pending",
        amount: 1490
    },
    {
        date: new Date(2026, 9, 1),
        no: "FTR-2026-0010",
        status: "paid",
        amount: 1490
    },
    {
        date: new Date(2026, 8, 1),
        no: "FTR-2026-0009",
        status: "paid",
        amount: 1490
    },
    {
        date: new Date(2026, 7, 1),
        no: "FTR-2026-0008",
        status: "failed",
        amount: 1490
    },
    {
        date: new Date(2026, 6, 1),
        no: "FTR-2026-0007",
        status: "paid",
        amount: 1290
    },
    {
        date: new Date(2026, 5, 1),
        no: "FTR-2026-0006",
        status: "paid",
        amount: 1290
    }
];

const STATUS_TYPES = {
    pending: "warning",
    failed: "danger",
    paid: "success"
} as const;

const MONTHLY_PRICE = 1490;

const YEARLY_PRICE = 1192;

const stylesheet = StyleSheet.create({
    struck: {
        textDecorationLine: "line-through"
    },
    head: {
        justifyContent: "space-between",
        alignItems: "flex-end",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    price: {
        alignItems: "baseline",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    split: {
        alignItems: "stretch",
        flexDirection: "row"
    },
    plans: {
        alignItems: "stretch",
        flexDirection: "row"
    },
    row: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    bar: {
        alignItems: "center",
        flexDirection: "row"
    },
    title: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    usage: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    metric: {
        flexBasis: 0,
        flexGrow: 1
    },
    card: {
        minWidth: 0,
        flex: 1
    },
    flex: {
        minWidth: 0,
        flex: 1
    },
    content: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const Billing01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        activePage,
        setActivePage
    ] = useState("billing");

    const [
        period,
        setPeriod
    ] = useState<Period>("yearly");

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isRoomy = width >= shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth;

    const isYearly = period === "yearly";

    const wholeCurrency = new Intl.NumberFormat(activeLocale, {
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const preciseCurrency = new Intl.NumberFormat(activeLocale, {
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        year: "numeric",
        month: "short",
        day: "numeric"
    });

    const usageFormat = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1
    });

    const percentFormat = new Intl.NumberFormat(activeLocale, {
        style: "percent"
    });

    const monthFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short",
        year: "numeric"
    });

    const handlePage = (page: string) => () => setActivePage(page);

    const handlePeriodChange = (value: string | Array<string>) => setPeriod(PERIODS.find((key) => key === value) ?? period);

    const renderNavItem = (page: string, icon: NCoreUIKitIcon, title: string, count?: number) => <Sidebar.MenuItem
        key={page}
    >
        <Sidebar.MenuButton
            isActive={activePage === page}
            onPress={handlePage(page)}
            title={title}
            icon={icon}
        />
        {
            count === undefined ?
                null
            :
                <Sidebar.MenuBadge
                    count={count}
                />
        }
    </Sidebar.MenuItem>;

    const renderUsage = (row: UsageRow) => {
        const percent = Math.round(row.used / row.limit * 100);

        const isWarning = percent >= WARNING_PERCENT;

        const value = `${usageFormat.format(row.used)} / ${usageFormat.format(row.limit)}${row.unit ? ` ${row.unit}` : ""}`;

        return <View
            style={[
                stylesheet.metric,
                {
                    minWidth: shapeConfigs.fieldMinWidth * 0.75,
                    gap: spaces.spacingSm
                }
            ]}
            key={row.id}
        >
            <View>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t(`blocks.billing01.usage_${row.id}`)}
                </Text>
                <View
                    style={[
                        stylesheet.title,
                        {
                            minHeight: controlHeights.small,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleMediumSize"
                        color="high"
                    >
                        {value}
                    </Text>
                    {
                        isWarning ?
                            <Badge
                                title={percentFormat.format(percent / 100)}
                                icon={TriangleAlertIcon}
                                type="warning"
                            />
                        :
                            null
                    }
                </View>
            </View>
            <Progress
                title={t(`blocks.billing01.usage_${row.id}`)}
                type={isWarning ? "warning" : "primary"}
                value={percent}
            >
                <Progress.Track/>
            </Progress>
        </View>;
    };

    const renderFeature = (feature: string) => <View
        style={[
            stylesheet.bar,
            {
                gap: spaces.spacingSm
            }
        ]}
        key={feature}
    >
        <Icon
            icon={Check}
            color="mid"
        />
        <View
            style={stylesheet.flex}
        >
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t(`blocks.billing01.${feature}`)}
            </Text>
        </View>
    </View>;

    const renderPlan = (plan: Plan) => {
        const isBusiness = plan.id === "business";

        const isStarter = plan.id === "starter";

        const price = isStarter ? 0 : isYearly ? YEARLY_PRICE : MONTHLY_PRICE;

        return <Card
            style={[
                stylesheet.card,
                plan.isCurrent ? {
                    borderColor: colors.content.border.emphasized,
                    borderWidth: borders.selection
                } : undefined
            ]}
            variant="outline"
            key={plan.id}
        >
            <Card.Header>
                <Card.Title>
                    {t(`blocks.billing01.plan_${plan.id}`)}
                </Card.Title>
                <Card.Description>
                    {t(`blocks.billing01.plan_${plan.id}_lead`)}
                </Card.Description>
                {
                    plan.isCurrent ?
                        <Card.Action>
                            <Badge
                                title={t("blocks.billing01.currentPlan")}
                                type="primary"
                            />
                        </Card.Action>
                    :
                        null
                }
            </Card.Header>
            <Card.Content>
                <View
                    style={{
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={[
                            stylesheet.price,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant={isBusiness ? "headlineSmallSize" : "headlineMediumSize"}
                            color="high"
                        >
                            {isBusiness ? t("blocks.billing01.contactUs") : wholeCurrency.format(price)}
                        </Text>
                        {
                            isBusiness ?
                                null
                            :
                                <Text
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.billing01.perMonth")}
                                </Text>
                        }
                        {
                            plan.isCurrent && isYearly ?
                                <Text
                                    style={stylesheet.struck}
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {wholeCurrency.format(MONTHLY_PRICE)}
                                </Text>
                            :
                                null
                        }
                    </View>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {
                            plan.isCurrent ?
                                t(isYearly ? "blocks.billing01.noteYearly" : "blocks.billing01.noteMonthly", [
                                    wholeCurrency.format(YEARLY_PRICE * 12)
                                ])
                            :
                                t(`blocks.billing01.plan_${plan.id}_note`)
                        }
                    </Text>
                    <Button
                        variant={plan.isCurrent ? "tonal" : isBusiness ? "filled" : "outline"}
                        title={t(`blocks.billing01.cta_${plan.id}`)}
                        type={isBusiness ? "primary" : "neutral"}
                        isDisabled={plan.isCurrent}
                        spreadBehaviour="stretch"
                        onPress={handlePress}
                    />
                    <Separator/>
                    <View
                        style={{
                            gap: spaces.spacingSm
                        }}
                    >
                        {plan.features.map(renderFeature)}
                    </View>
                </View>
            </Card.Content>
        </Card>;
    };

    const renderInvoice = (invoice: Invoice) => <Table.Row
        key={invoice.no}
    >
        <Table.Cell>
            {invoice.no}
        </Table.Cell>
        <Table.Cell>
            {dateFormat.format(invoice.date)}
        </Table.Cell>
        <Table.Cell>
            {monthFormat.format(invoice.date)}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {preciseCurrency.format(invoice.amount)}
        </Table.Cell>
        <Table.Cell>
            <Badge
                title={t(`blocks.billing01.status_${invoice.status}`)}
                type={STATUS_TYPES[invoice.status]}
            />
        </Table.Cell>
        <Table.Cell
            isShrink={true}
        >
            <Button
                accessibilityLabel={t("blocks.billing01.downloadInvoice", [
                    invoice.no
                ])}
                onPress={handlePress}
                icon={DownloadIcon}
                variant="ghost"
                type="neutral"
                size="small"
            />
        </Table.Cell>
    </Table.Row>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            collapseBehaviour={isRoomy ? "offcanvas" : "icon"}
            title={t("blocks.billing01.navTitle")}
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group>
                    <Sidebar.Menu>
                        {renderNavItem("home", LayoutDashboardIcon, t("blocks.billing01.navHome"))}
                        {renderNavItem("projects", FolderIcon, t("blocks.billing01.navProjects"))}
                        {renderNavItem("team", UsersIcon, t("blocks.billing01.navTeam"), 18)}
                        {renderNavItem("integrations", PlugIcon, t("blocks.billing01.navIntegrations"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.billing01.account")}
                >
                    <Sidebar.Menu>
                        {renderNavItem("settings", SettingsIcon, t("blocks.billing01.navSettings"))}
                        {renderNavItem("billing", CreditCardIcon, t("blocks.billing01.navBilling"))}
                        {renderNavItem("security", ShieldCheckIcon, t("blocks.billing01.navSecurity"))}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.billing01.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.billing01.userEmail")}
                            title={t("blocks.billing01.userName")}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Sidebar.Trigger/>
                <View
                    style={stylesheet.flex}
                >
                    {
                        isCompact ?
                            null
                        :
                            <Breadcrumb
                                accessibilityLabel={t("blocks.billing01.breadcrumb")}
                                compactBehaviour="collapse"
                            >
                                <Breadcrumb.List>
                                    <Breadcrumb.Item>
                                        <Breadcrumb.Link
                                            title={t("blocks.billing01.account")}
                                            onPress={handlePress}
                                        />
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item>
                                        <Breadcrumb.Page
                                            title={t("blocks.billing01.navBilling")}
                                        />
                                    </Breadcrumb.Item>
                                </Breadcrumb.List>
                            </Breadcrumb>
                    }
                </View>
                <Badge.Indicator
                    type="danger"
                >
                    <Button
                        accessibilityLabel={t("blocks.billing01.notifications")}
                        onPress={handlePress}
                        variant="ghost"
                        icon={BellIcon}
                        type="neutral"
                        size="small"
                    />
                </Badge.Indicator>
            </View>
            <Separator/>
            <View
                style={[
                    stylesheet.content,
                    {
                        padding: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={{
                        gap: spaces.spacingXs
                    }}
                >
                    <Text
                        variant="headlineMediumSize"
                        accessibilityRole="header"
                        color="high"
                    >
                        {t("blocks.billing01.title")}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.billing01.description")}
                    </Text>
                </View>
                <View
                    style={[
                        isRoomy ? stylesheet.split : undefined,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Card
                        style={stylesheet.card}
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.billing01.currentTitle")}
                            </Card.Title>
                            <Card.Description>
                                {t("blocks.billing01.renewal")}
                            </Card.Description>
                            <Card.Action>
                                <View
                                    style={[
                                        stylesheet.price,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <Text
                                        variant="headlineMediumSize"
                                        color="high"
                                    >
                                        {wholeCurrency.format(MONTHLY_PRICE)}
                                    </Text>
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.billing01.perMonth")}
                                    </Text>
                                </View>
                            </Card.Action>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <Separator/>
                                <View
                                    style={[
                                        stylesheet.usage,
                                        {
                                            gap: spaces.spacingLg
                                        }
                                    ]}
                                >
                                    {USAGE.map(renderUsage)}
                                </View>
                            </View>
                        </Card.Content>
                    </Card>
                    <Card
                        style={[
                            stylesheet.card,
                            isRoomy ? {
                                maxWidth: shapeConfigs.cardWidth
                            } : undefined
                        ]}
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.billing01.payment")}
                            </Card.Title>
                            <Card.Action>
                                <Button
                                    title={t("blocks.billing01.billingEmail")}
                                    onPress={handlePress}
                                    variant="link"
                                    type="primary"
                                    size="small"
                                />
                            </Card.Action>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <Item>
                                    <Item.Media
                                        icon={VisaBrandIcon}
                                        variant="icon"
                                    />
                                    <Item.Content>
                                        <Item.Title>
                                            {t("blocks.billing01.cardNumber")}
                                        </Item.Title>
                                        <Item.Description>
                                            {t("blocks.billing01.cardExpiry")}
                                        </Item.Description>
                                    </Item.Content>
                                    <Item.Actions>
                                        <Button
                                            title={t("blocks.billing01.change")}
                                            onPress={handlePress}
                                            variant="outline"
                                            type="neutral"
                                            size="small"
                                        />
                                    </Item.Actions>
                                </Item>
                                <Separator/>
                                <View
                                    style={{
                                        gap: spaces.spacingXs
                                    }}
                                >
                                    <View
                                        style={stylesheet.head}
                                    >
                                        <Text
                                            variant="labelMediumSize"
                                            color="high"
                                        >
                                            {t("blocks.billing01.address")}
                                        </Text>
                                        <Button
                                            title={t("blocks.billing01.edit")}
                                            onPress={handlePress}
                                            variant="link"
                                            type="primary"
                                            size="small"
                                        />
                                    </View>
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.billing01.addressText")}
                                    </Text>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.billing01.taxNumber")}
                                    </Text>
                                </View>
                            </View>
                        </Card.Content>
                    </Card>
                </View>
                <View
                    style={{
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={[
                            stylesheet.head,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.flex,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                variant="titleLargeSize"
                                color="high"
                            >
                                {t("blocks.billing01.plans")}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.billing01.plansLead")}
                            </Text>
                        </View>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Badge
                                title={t("blocks.billing01.save")}
                                spreadBehaviour="free"
                                type="success"
                            />
                            <View>
                                <ToggleGroup
                                    title={t("blocks.billing01.periodTitle")}
                                    onValueChange={handlePeriodChange}
                                    attachBehaviour="attached"
                                    variant="outline"
                                    value={period}
                                    size="small"
                                >
                                    <ToggleGroup.Item
                                        title={t("blocks.billing01.monthly")}
                                        value="monthly"
                                    />
                                    <ToggleGroup.Item
                                        title={t("blocks.billing01.yearly")}
                                        value="yearly"
                                    />
                                </ToggleGroup>
                            </View>
                        </View>
                    </View>
                    <View
                        style={[
                            isCompact ? undefined : stylesheet.plans,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {PLANS.map(renderPlan)}
                    </View>
                    {
                        isYearly ?
                            <Alert
                                title={t("blocks.billing01.switchNotice", [
                                    wholeCurrency.format(YEARLY_PRICE * 12)
                                ])}
                                type="info"
                            />
                        :
                            null
                    }
                </View>
                <View
                    style={{
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={[
                            stylesheet.head,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            variant="titleLargeSize"
                            color="high"
                        >
                            {t("blocks.billing01.invoices")}
                        </Text>
                        <Button
                            title={t("blocks.billing01.downloadAll")}
                            onPress={handlePress}
                            icon={DownloadIcon}
                            variant="outline"
                            type="neutral"
                            size="small"
                        />
                    </View>
                    <Table
                        caption={t("blocks.billing01.invoices")}
                        isCaptionHidden={true}
                        isRaised={true}
                    >
                        <Table.Header>
                            <Table.Row>
                                <Table.Head>
                                    {t("blocks.billing01.col_no")}
                                </Table.Head>
                                <Table.Head>
                                    {t("blocks.billing01.col_date")}
                                </Table.Head>
                                <Table.Head>
                                    {t("blocks.billing01.col_period")}
                                </Table.Head>
                                <Table.Head
                                    isNumeric={true}
                                >
                                    {t("blocks.billing01.col_amount")}
                                </Table.Head>
                                <Table.Head>
                                    {t("blocks.billing01.col_status")}
                                </Table.Head>
                                <Table.Head
                                    isShrink={true}
                                >
                                    <View
                                        style={{
                                            width: controlHeights.small
                                        }}
                                    />
                                </Table.Head>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {INVOICES.map(renderInvoice)}
                        </Table.Body>
                    </Table>
                </View>
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Billing01;
